import { createLinearGradient } from "../color/color"
import { LayerTypeGroupId, LayerTypeSubgroupId, TerraMapLayerType } from "../generated/enums"
import { labels } from "../generated/labels"
import { nutrientColors } from "../nutrition/colors"
import { groupBy, partition, selectMany } from "../utils/array"
import { IDataLayer, IDataLayerType, INumericValuesViewerOptions } from "./types"

import { LayerTypeId as TypeId  } from "../generated/enums"

export function groupTerraMapLayers(types: IDataLayerType[], layers: IDataLayer[]) {

    const groups = groupBy(types, t => t.SubgroupId || LayerTypeSubgroupId.TerraMapOther).sort((a, b) => a.key - b.key)

    return selectMany(groups.map(g => {

        const name = labels.LayerTypeSubgroupId(g.key)

        const groupLayers = layers.filter(l => g.values.some(t => l.TypeId == t.TypeId))

        const [ext, so] = partition(groupLayers, l => l.TerraMap == TerraMapLayerType.External)

        return (ext.length && so.length)
            ? [
                { id: "terramap-group-so", name: name + " - SoilOptix", types: g.values, layers: so },
                { id: "terramap-group-ext", name: name + " - external", types: g.values, layers: ext },
            ]
            : [
                { id: "terramap-group", name, types: g.values, layers: groupLayers },
            ]

    }))

}


export function getTerraMapDynamicViewerOptions(type: IDataLayerType): INumericValuesViewerOptions {

    const { colors, match } = getTerraMapDynamicColors(type)

    type = !colors
        ? type
        : {
            ...type,
            Ranges: undefined,
            Style: {
                Gradient: colors
            }
        }

    return {
        type,
        layerFilter: l => false,
        showStroke: match ? match.showStroke : undefined,
        debug: false
    }

}


export function getTerraMapDynamicColors(type: IDataLayerType) {

    const match = dynamicColors.find(c => c.type && (c.type == type.TypeId))
        || dynamicColors.find(c => (!c.type) && (c.group == type.GroupId) && (c.subgroup == type.SubgroupId))

    const colors = match
        ? match.colors
        : type.Ranges
            ? type.Ranges.map(r => r.FillColor)
            : undefined


    return { colors, match }
}

const colorPalettes = {

    P2O5: getNutritionColorGradient("P2O5"),
    K2O: getNutritionColorGradient("K2O"),
    MgO: getNutritionColorGradient("MgO"),

    pH: createLinearGradient([[72, 16, 70], [253, 248, 253]], 10),

    default: createLinearGradient([[122, 1, 119], [254, 235, 254]], 10).reverse()

}

const dynamicColors = [

    { type: TypeId.P2O5, colors: colorPalettes.P2O5 },
    { type: TypeId.K2O, colors: colorPalettes.K2O },
    { type: TypeId.MgO, colors: colorPalettes.MgO },

    { type: TypeId.TotalP2O5, colors: colorPalettes.P2O5 },
    { type: TypeId.TotalK2O, colors: colorPalettes.K2O },
    { type: TypeId.TotalMgO, colors: colorPalettes.MgO },

    { type: TypeId.AvailableP2O5, colors: colorPalettes.P2O5 },
    { type: TypeId.AvailableK2O, colors: colorPalettes.K2O },
    { type: TypeId.AvailableMgO, colors: colorPalettes.MgO },

    { type: TypeId.pH, colors: colorPalettes.pH, showStroke: true },
    { type: TypeId.BufferpH, colors: colorPalettes.pH, showStroke: true },

    { type: TypeId.TotalCa, colors: colorPalettes.default },
    { type: TypeId.TotalNa, colors: colorPalettes.default },
    { type: TypeId.TotalSO3, colors: colorPalettes.default },
    { type: TypeId.TotalCaMgRatio, colors: colorPalettes.default },

    { type: TypeId.AvailableCa, colors: colorPalettes.default },
    { type: TypeId.AvailableNa, colors: colorPalettes.default },
    { type: TypeId.AvailableSO3, colors: colorPalettes.default },

    { group: LayerTypeGroupId.TerraMap, subgroup: LayerTypeSubgroupId.TerraMapAdditional, colors: colorPalettes.default },

    { type: TypeId.SOCClay, colors: createLinearGradient([[122, 1, 119], [254, 235, 254]], 10), showStroke: false },

]


function getNutritionColorGradient(key: "P2O5" | "K2O" | "MgO") {
    const g = nutrientColors[key].gradient.map(g => g.Colour)
    return createLinearGradient(g, 10)
}
